import { useEffect, useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandFavicon } from '../BrandFavicon';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../../utils/help/help-content';
import { SettingsCard } from './SettingsCard';

interface DomainItem {
  id: string;
  host: string;
  isPrimary: boolean;
  isTrackingSource: boolean;
}

interface AliasItem {
  id: string;
  name: string;
  isPrimary: boolean;
}

export function BrandIdentitySection({
  domains,
  aliases,
  loading,
  onAddDomain,
  onAddAlias,
  onUpdateDomain,
  onUpdateAlias,
  onDeleteAlias,
  updatingDomainId,
  updatingAliasId,
  deletingAliasId,
}: {
  domains: DomainItem[];
  aliases: AliasItem[];
  loading: boolean;
  onAddDomain: (host: string) => Promise<void>;
  onAddAlias: (name: string) => Promise<void>;
  onUpdateDomain: (domainId: string, host: string) => Promise<void>;
  onUpdateAlias: (aliasId: string, name: string) => Promise<void>;
  onDeleteAlias: (aliasId: string) => Promise<void>;
  updatingDomainId: string | null;
  updatingAliasId: string | null;
  deletingAliasId: string | null;
}) {
  const [host, setHost] = useState('');
  const [aliasName, setAliasName] = useState('');

  async function handleAddDomain(e: React.FormEvent) {
    e.preventDefault();
    const value = host.trim();
    if (!value) return;
    await onAddDomain(value);
    setHost('');
  }

  async function handleAddAlias(e: React.FormEvent) {
    e.preventDefault();
    const value = aliasName.trim();
    if (!value) return;
    await onAddAlias(value);
    setAliasName('');
  }

  return (
    <SettingsCard
      title="هویت برند"
      helpKey="brand_identity"
      description="وب‌سایت و نام‌هایی که برند شما ممکن است در پاسخ‌های AI با آن‌ها دیده شود را وارد کنید — مثلاً mizfa.tools با نام‌های مستعار مثل Mizfa Tools یا میزفا تولز."
    >
      <Row className="g-4">
        <Col lg={6}>
          <div className="mb-3">
            <h3 className="fs--1 fw-medium text-800">
              <span className="d-inline-flex align-items-center gap-1">
                دامنه‌های ردیابی
                <HelpTooltip html={getHelpHtml('tracking_domains')} />
              </span>
            </h3>
            <p className="mt-1 fs--2 text-600">
              وب‌سایت شما که هنگام اجرای پرامپت‌ها به‌عنوان زمینه اضافه می‌شود. این به AI کمک می‌کند منظور شما
              از کدام سایت یا محصول است — برای امتیازدهی دیده‌شدن استفاده نمی‌شود.
            </p>
            <p className="mt-1 fs--2 text-600">
              مثال: <span className="fw-medium text-700">mizfa.tools</span> یا{' '}
              <span className="fw-medium text-700">https://mizfa.tools</span>
            </p>
          </div>
          <Form onSubmit={e => void handleAddDomain(e)} className="mb-3 d-flex gap-2">
            <Form.Control
              size="sm"
              value={host}
              onChange={e => setHost(e.target.value)}
              placeholder="mizfa.tools"
            />
            <Button type="submit" size="sm" variant="outline-primary" disabled={loading || !host.trim()} className="flex-shrink-0">
              افزودن
            </Button>
          </Form>
          {domains.length === 0 ? (
            <EmptyHint text="دامنه وب‌سایت خود را اضافه کنید تا اجراهای AI بدانند برند شما کدام است — مثلاً mizfa.tools." />
          ) : (
            <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
              {domains.map((domain, index) => (
                <DomainRow
                  key={domain.id}
                  domain={domain}
                  colorIndex={index}
                  updating={updatingDomainId === domain.id}
                  onUpdate={onUpdateDomain}
                />
              ))}
            </ul>
          )}
        </Col>

        <Col lg={6}>
          <div className="mb-3">
            <h3 className="fs--1 fw-medium text-800">
              <span className="d-inline-flex align-items-center gap-1">
                نام‌های مستعار برند
                <HelpTooltip html={getHelpHtml('brand_aliases')} />
              </span>
            </h3>
            <p className="mt-1 fs--2 text-600">
              املاها یا نام‌های دیگری که ممکن است برای برند شما (و AI) استفاده شود. از این‌ها برای
              شناسایی منشن برند شما در پاسخ‌ها استفاده می‌کنیم.
            </p>
            <p className="mt-1 fs--2 text-600">
              مثال: <span className="fw-medium text-700">Mizfa Tools</span>,{' '}
              <span className="fw-medium text-700">میزفا تولز</span>,{' '}
              <span className="fw-medium text-700">mizfa tools</span>
            </p>
          </div>
          <Form onSubmit={e => void handleAddAlias(e)} className="mb-3 d-flex gap-2">
            <Form.Control
              size="sm"
              value={aliasName}
              onChange={e => setAliasName(e.target.value)}
              placeholder="Mizfa Tools"
            />
            <Button type="submit" size="sm" variant="outline-primary" disabled={loading || !aliasName.trim()} className="flex-shrink-0">
              افزودن
            </Button>
          </Form>
          {aliases.length === 0 ? (
            <EmptyHint text="نحوه ظاهر شدن برند شما در متن AI را اضافه کنید — مثلاً Mizfa Tools یا میزفا تولز." />
          ) : (
            <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0">
              {aliases.map(alias => (
                <AliasTag
                  key={alias.id}
                  alias={alias}
                  updating={updatingAliasId === alias.id}
                  deleting={deletingAliasId === alias.id}
                  onUpdate={onUpdateAlias}
                  onDelete={onDeleteAlias}
                />
              ))}
            </ul>
          )}
        </Col>
      </Row>
    </SettingsCard>
  );
}

function DomainRow({
  domain,
  colorIndex,
  updating,
  onUpdate,
}: {
  domain: DomainItem;
  colorIndex: number;
  updating: boolean;
  onUpdate: (domainId: string, host: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [host, setHost] = useState(domain.host);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setHost(domain.host);
  }, [domain.host, editing]);

  function cancelEdit() {
    setHost(domain.host);
    setEditing(false);
  }

  async function saveEdit() {
    const trimmed = host.trim();
    if (!trimmed || trimmed === domain.host) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onUpdate(domain.id, trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <li className="d-flex align-items-center gap-2 rounded-3 border border-primary-subtle bg-primary-subtle px-2 py-2">
        <Form.Control
          size="sm"
          value={host}
          onChange={e => setHost(e.target.value)}
          disabled={saving || updating}
          autoFocus
          onKeyDown={e => {
            if (e.key === 'Enter') void saveEdit();
            if (e.key === 'Escape') cancelEdit();
          }}
        />
        <Button type="button" size="sm" variant="primary" disabled={saving || updating} onClick={() => void saveEdit()}>
          ذخیره
        </Button>
        <Button type="button" size="sm" variant="outline-secondary" disabled={saving || updating} onClick={cancelEdit}>
          انصراف
        </Button>
      </li>
    );
  }

  return (
    <li className="d-flex align-items-center gap-3 rounded-3 border bg-100 dark__bg-1100 px-3 py-2">
      <BrandFavicon host={domain.host} name={domain.host} colorIndex={colorIndex} />
      <div className="min-w-0 flex-grow-1">
        <p className="text-truncate fw-medium text-1000 mb-0">{domain.host}</p>
      </div>
      {domain.isPrimary && (
        <span className="flex-shrink-0 badge badge-subtle-primary">اصلی</span>
      )}
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={() => setEditing(true)}
        className="flex-shrink-0 text-500 p-1"
        aria-label={`ویرایش ${domain.host}`}
        title="ویرایش دامنه"
      >
        <FontAwesomeIcon icon="pen" className="fs--2" />
      </Button>
    </li>
  );
}

function AliasTag({
  alias,
  updating,
  deleting,
  onUpdate,
  onDelete,
}: {
  alias: AliasItem;
  updating: boolean;
  deleting: boolean;
  onUpdate: (aliasId: string, name: string) => Promise<void>;
  onDelete: (aliasId: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(alias.name);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setName(alias.name);
  }, [alias.name, editing]);

  function cancelEdit() {
    setName(alias.name);
    setEditing(false);
  }

  async function saveEdit() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === alias.name) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onUpdate(alias.id, trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <li className="d-flex align-items-center gap-1 rounded-pill border border-primary-subtle bg-primary-subtle py-1 ps-2 pe-1">
        <Form.Control
          size="sm"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={saving || updating}
          autoFocus
          style={{ width: '8rem' }}
          onKeyDown={e => {
            if (e.key === 'Enter') void saveEdit();
            if (e.key === 'Escape') cancelEdit();
          }}
        />
        <Button type="button" size="sm" variant="primary" disabled={saving || updating} onClick={() => void saveEdit()}>
          ذخیره
        </Button>
        <Button type="button" size="sm" variant="link" className="text-500" disabled={saving || updating} onClick={cancelEdit}>
          انصراف
        </Button>
      </li>
    );
  }

  return (
    <li>
      <span className="d-inline-flex align-items-center gap-1 rounded-pill border bg-white dark__bg-card-dark py-1 ps-3 pe-1 fs--1 text-700">
        <span className="text-truncate" style={{ maxWidth: 180 }}>
          {alias.name}
        </span>
        {alias.isPrimary && <span className="badge badge-subtle-secondary">اصلی</span>}
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={() => setEditing(true)}
          className="rounded-circle p-1 text-500"
          aria-label={`ویرایش ${alias.name}`}
          title="ویرایش نام مستعار"
        >
          <FontAwesomeIcon icon="pen" className="fs--2" />
        </Button>
        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={deleting}
          onClick={() => void onDelete(alias.id)}
          className="rounded-circle p-1 text-500"
          aria-label={`حذف ${alias.name}`}
          title="حذف نام مستعار"
        >
          <FontAwesomeIcon icon="times" className="fs--2" />
        </Button>
      </span>
    </li>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="rounded-3 border border-dashed px-4 py-4 text-center fs--1 text-600 mb-0">{text}</p>;
}
